import { makeObservable, observable, action, computed, runInAction } from "mobx";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import {
  type ImportableNote,
  ImportStatus,
  LoadingStatus,
  type OneClickImportEntryPoint,
  OneClickImportSource,
} from "./types";
import { SectionSelectionState } from "./types";
import type { Uuid } from "@/domains/global/identifiers";
import { uuidModule } from "@/modules/uuid";
import type { FileDropItemWithPath } from "@/modules/file/dropItems";
import { pluralize } from "@/modules/pluralize";
import {
  markImportSessionInProgress,
  clearImportSessionInProgress,
} from "@/modules/cache/import-session";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { objectModule } from "@/modules/object";
import { SortByKind } from "@/modules/lenses/types";
import uFuzzy from "@leeoniya/ufuzzy";
import { electronModule } from "@/modules/electron";
import { logger } from "@/modules/logger";
import type { ImportedAppleNote } from "@/modules/electron/types";
import { ImportFileUploaderKind } from "@/store/upload-progress/AppStoreUploadProgressStore";
import {
  uploadAttachments,
  buildFileAttachmentHtml,
  type UploadableAttachment,
  type SkippedAttachment,
} from "@/modules/file-import/upload";
import { toastModule } from "@/modules/toast";

/**
 * Result of the importSelectedNotes operation
 */
export interface ImportResult {
  success: boolean;
  importedCount: number;
  failedCount: number;
  totalSelected: number;
  sessionId: string | null;
}

const isAppleNoteId = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

/**
 * Sort options supported for imported notes
 */
export type ImportedNoteSortBy =
  | SortByKind.LastModified
  | SortByKind.LastCreated
  | SortByKind.Alphabetical;

/**
 * Represents a folder with its associated collection ID for import
 */
export interface ImportableFolder {
  name: string;
  collectionId: Uuid;
}

/**
 * Represents a badge to display for a note (folder or tag)
 */
export interface NoteCollectionBadge {
  type: "folder" | "tag";
  name: string;
  collectionId?: Uuid;
}

/**
 * Represents a folder grouping for display in the import UI.
 * Groups by the last (deepest) folder in the path.
 */
export interface FolderGroup {
  /** The section name (last folder in path, or empty for unfiled) */
  section: string;
  /** Collection IDs for all folders in the path */
  collectionIds: Uuid[];
  notes: ImportableNote[];
}

/**
 * Store for managing one-click import of notes from external sources (e.g., Apple Notes)
 */
export class AppStoreOneClickImportStore extends AppSubStore {
  /**
   * The default Apple Notes folder name that should be filtered out during import.
   * Notes in this folder are considered "unfiled" in the Mem import context.
   */
  static readonly DEFAULT_APPLE_NOTES_FOLDER = "Notes";

  /**
   * Current status of the import process
   */
  importStatus: ImportStatus = ImportStatus.Idle;

  /**
   * Status of loading notes from Apple Notes
   */
  loadingStatus: LoadingStatus = LoadingStatus.Done;

  /**
   * Error message to display in the modal footer
   */
  errorMessage: string | null = null;

  /**
   * List of notes available for import
   */
  notes: ImportableNote[] = [];

  /**
   * Cached array of note titles for fuzzy search (updated when notes are added)
   */
  private noteTitles: string[] = [];

  /**
   * Whether to use folders as collections when importing
   */
  useFoldersAsCollections = true;

  /**
   * Whether to use tags as collections when importing
   */
  useTagsAsCollections = true;

  /**
   * Selected folder names to convert to collections (INCLUDE list)
   * Empty array means no folders selected, even if useFoldersAsCollections is true
   */
  selectedFolderNames: string[] = [];

  /**
   * Selected tag names to convert to collections (INCLUDE list)
   * Empty array means no tags selected, even if useTagsAsCollections is true
   */
  selectedTagNames: string[] = [];

  /**
   * Whether the import modal is open
   */
  isModalOpen = false;

  /**
   * Whether notes are currently being uploaded (during import phase)
   */
  isImporting = false;

  /**
   * Entry point that initiated the import (for analytics)
   */
  entryPoint: OneClickImportEntryPoint | null = null;

  /**
   * Source application for the import (for analytics)
   */
  source: OneClickImportSource = OneClickImportSource.AppleNotes;

  /**
   * Filter query for searching notes by title
   */
  filterQuery = "";

  /**
   * Search query for filtering tags in the dropdown
   */
  tagSearchQuery = "";

  /**
   * Search query for filtering folders in the dropdown
   */
  folderSearchQuery = "";

  /**
   * Sort option for notes in the import list
   */
  sortOption: ImportedNoteSortBy = SortByKind.LastModified;

  /**
   * Map of folder names to their generated collection IDs
   */
  private folderToCollectionId: Map<string, Uuid> = new Map();

  /**
   * Map of tag names to their generated collection IDs
   */
  private tagToCollectionId: Map<string, Uuid> = new Map();

  /**
   * Fuzzy search instance for filtering notes
   */
  private fuzzy: uFuzzy;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.fuzzy = new uFuzzy();

    makeObservable<
      AppStoreOneClickImportStore,
      | "noteTitles"
      | "folderToCollectionId"
      | "tagToCollectionId"
      | "fuzzy"
      | "getOrCreateCollectionId"
      | "getOrCreateTagCollectionId"
      | "filterDefaultAppleNotesFolder"
      | "getSectionForNote"
      | "createFileDropItemFromNote"
      | "normalizeFolderNameForHashtag"
      | "normalizeTagNameForHashtag"
      | "sanitizeFolderNameForPath"
      | "sanitizeFilename"
      | "replaceAttachmentPlaceholders"
      | "trackImportComplete"
      | "getAttachmentStats"
      | "processUploadFailure"
      | "loadAttachments"
      | "setupSessionForUpload"
    >(this, {
      importStatus: observable,
      loadingStatus: observable,
      errorMessage: observable,
      notes: observable,
      noteTitles: observable,
      useFoldersAsCollections: observable,
      useTagsAsCollections: observable,
      selectedFolderNames: observable,
      selectedTagNames: observable,
      isModalOpen: observable,
      isImporting: observable,
      entryPoint: observable,
      source: observable,
      filterQuery: observable,
      tagSearchQuery: observable,
      folderSearchQuery: observable,
      sortOption: observable,
      folderToCollectionId: observable,
      tagToCollectionId: observable,
      fuzzy: false,
      getOrCreateCollectionId: false,
      getOrCreateTagCollectionId: false,
      getCollectionIdForFolder: false,
      getCollectionIdForTag: false,
      getNotesCountForFolder: false,
      getNotesCountForTag: false,
      getBadgesForNote: false,
      filterDefaultAppleNotesFolder: false,
      getSectionForNote: false,
      createFileDropItemFromNote: false,
      normalizeFolderNameForHashtag: false,
      normalizeTagNameForHashtag: false,
      sanitizeFolderNameForPath: false,
      sanitizeFilename: false,
      replaceAttachmentPlaceholders: false,
      openModal: action,
      closeModal: action,
      closeAppleNotesReader: false,
      resetImportStatus: action,
      startImport: action,
      loadNotes: action,
      retryLoadNotes: action,
      setLoadingStatus: action,
      setErrorMessage: action,
      clearErrorMessage: action,
      isLoading: computed,
      hasLoadingError: computed,
      setFilterQuery: action,
      setTagSearchQuery: action,
      setFolderSearchQuery: action,
      setSortOption: action,
      toggleUseFoldersAsCollections: action,
      toggleUseTagsAsCollections: action,
      setSelectedFolderNames: action,
      setSelectedTagNames: action,
      toggleNoteSelection: action,
      selectAll: action,
      unselectAll: action,
      toggleSectionSelection: action,
      selectSection: action,
      unselectSection: action,
      addNote: action,
      addNotes: action,
      clear: action,
      setIsImporting: action,
      importSelectedNotes: action,
      selectedNotes: computed,
      selectedNotesCount: computed,
      totalNotesCount: computed,
      selectableNotesCount: computed,
      sortedNotes: computed,
      filteredNotes: computed,
      folders: computed,
      tags: computed,
      filteredFolders: computed,
      filteredTags: computed,
      importableFolders: computed,
      importableTags: computed,
      notesGroupedByFolder: computed,
      getSectionSelectionState: false,
      setStatus: action,
      trackImportComplete: false,
      getAttachmentStats: false,
      processUploadFailure: false,
      loadAttachments: false,
      setupSessionForUpload: false,
    });
  }

  /**
   * Open the import modal and initialize selections
   */
  openModal = () => {
    this.isModalOpen = true;
  };

  /**
   * Close the import modal
   */
  closeModal = ({ status }: { status: ImportStatus }) => {
    this.isModalOpen = false;
    this.filterQuery = "";
    this.sortOption = SortByKind.LastModified;
    this.setStatus(status);
  };

  /**
   * Close the Apple Notes reader process.
   * Called automatically by closeModal, but can also be called directly
   * when canceling from contexts that don't use the modal (e.g., wizard step).
   */
  closeAppleNotesReader = async (): Promise<void> => {
    logger.debug({ message: "[OneClickImport] Closing Apple Notes Reader" });
    await electronModule.closeAppleNotesReader();
  };

  /**
   * Reset the import status to idle
   */
  resetImportStatus = () => {
    this.importStatus = ImportStatus.Idle;
  };

  setStatus(status: ImportStatus) {
    this.importStatus = status;
  }

  /**
   * Set the loading status for notes
   */
  setLoadingStatus = (status: LoadingStatus) => {
    this.loadingStatus = status;
  };

  /**
   * Set the error message to display in the modal
   */
  setErrorMessage = (message: string) => {
    this.errorMessage = message;
  };

  /**
   * Clear the error message
   */
  clearErrorMessage = () => {
    this.errorMessage = null;
  };

  /**
   * Set whether notes are currently being uploaded
   */
  setIsImporting = (value: boolean) => {
    this.isImporting = value;
  };

  /**
   * Whether notes are currently being loaded
   */
  get isLoading(): boolean {
    return this.loadingStatus === LoadingStatus.Loading;
  }

  /**
   * Whether there was an error loading notes
   */
  get hasLoadingError(): boolean {
    return this.loadingStatus === LoadingStatus.Error;
  }

  /**
   * Start the one-click import process for Apple Notes.
   * Opens the modal immediately (unless openModal is false) and starts loading notes in the background.
   * @param entryPoint - Where the import was initiated from (for analytics)
   * @param options.openModal - Whether to open the standalone modal (default: true).
   *                            Set to false when rendering content inside another container (e.g., wizard step).
   * @returns Promise that resolves to true if import started successfully, false otherwise
   */
  startImport = async (
    entryPoint: OneClickImportEntryPoint,
    options?: { openModal?: boolean }
  ): Promise<boolean> => {
    const shouldOpenModal = options?.openModal ?? true;

    logger.info({
      message: "[AppStoreOneClickImportStore] startImport",
      info: { entryPoint, openModal: shouldOpenModal },
    });

    // Reset all state for a fresh import
    this.clear();

    // Track entry point for analytics (after clear so it's not wiped)
    this.entryPoint = entryPoint;

    // Set loading state
    this.setStatus(ImportStatus.InProgress);
    this.setLoadingStatus(LoadingStatus.Loading);

    // Only open the standalone modal if requested
    if (shouldOpenModal) {
      this.openModal();
    }

    // Load notes in the background
    return this.loadNotes();
  };

  /**
   * Load notes from Apple Notes via Electron IPC.
   * This is called internally by startImport and retryLoadNotes.
   * @returns Promise that resolves to true if notes loaded successfully, false otherwise
   */
  loadNotes = async (): Promise<boolean> => {
    logger.info({
      message: "[AppStoreOneClickImportStore] loadNotes",
    });

    try {
      this.setLoadingStatus(LoadingStatus.Loading);

      const result = await electronModule.importAppleNotes({ collectAttachments: false });

      if (!result.success) {
        this.setLoadingStatus(LoadingStatus.Error);
        this.setErrorMessage("Failed to load notes from Apple Notes. Please try again.");
        this.setStatus(ImportStatus.Failed);

        logger.error({
          message: "[AppStoreOneClickImportStore] Apple Notes ICP failed",
          info: {
            error: result.error ?? "Unknown error",
            count: result.notes.length,
          },
        });

        trackEvent(TrackedEvent.OneClickImportLoadFailed, {
          source: this.source,
          entry_point: this.entryPoint ?? "profile_menu",
          error: result.error ?? "Unknown error",
        });

        return false;
      }

      logger.info({
        message: "[AppStoreOneClickImportStore] Apple Notes imported",
        info: { notesPerTag: result.notesPerTag, notesCount: result.notes.length },
      });

      // Convert ImportedAppleNote[] to ImportableNote[]
      const importableNotes = result.notes.map((note: ImportedAppleNote) => {
        // Convert folderPath (e.g., "Work/Mem/Auto Organize") to folders array

        const rawFolders = note.folderPath
          ? note.folderPath.split("/").filter(Boolean)
          : note.folder
            ? [note.folder]
            : [];
        const folders = this.filterDefaultAppleNotesFolder(rawFolders);

        return {
          appleNoteId: isAppleNoteId(note.id) ? note.id : undefined,
          title: note.title,
          markdown: note.content,
          folders,
          isSelected: true,
          isLocked: false,
          isPinned: note.isPinned ?? false,
          createdAt: note.createdAt,
          updatedAt: note.modifiedAt,
          tags: note.tags ?? [],
          subtitle: note.content.split("\n")[0]?.substring(0, 100) || undefined,
          attachments: undefined,
          skippedAttachments: note.skippedAttachments ?? [],
        };
      });

      // Add imported notes (state was already cleared by startImport)
      this.addNotes(importableNotes);

      if (this.selectedFolderNames.length === 0) {
        this.selectedFolderNames = this.folders;
      }

      if (this.selectedTagNames.length === 0) {
        this.selectedTagNames = this.tags;
      }

      // Set to what user has in Apple Notes at the time of import
      this.setSortOption(SortByKind.LastModified);
      this.setLoadingStatus(LoadingStatus.Done);

      return true;
    } catch (error) {
      this.setLoadingStatus(LoadingStatus.Error);
      this.setErrorMessage("Failed to load notes from Apple Notes. Please try again.");
      this.setStatus(ImportStatus.Failed);

      const errorMessage = error instanceof Error ? error.message : "Unknown exception";

      logger.error({
        message: "[AppStoreOneClickImportStore] Failed to import Apple Notes",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });

      trackEvent(TrackedEvent.OneClickImportLoadFailed, {
        source: this.source,
        entry_point: this.entryPoint ?? "profile_menu",
        error: errorMessage,
      });

      return false;
    }
  };

  /**
   * Retry loading notes after a failure
   */
  retryLoadNotes = async (): Promise<boolean> => {
    return this.loadNotes();
  };

  /**
   * Set the filter query for searching notes by title
   */
  setFilterQuery = (query: string) => {
    this.filterQuery = query;
  };

  /**
   * Set the search query for filtering tags
   */
  setTagSearchQuery = (query: string) => {
    this.tagSearchQuery = query;
  };

  /**
   * Set the search query for filtering folders
   */
  setFolderSearchQuery = (query: string) => {
    this.folderSearchQuery = query;
  };

  /**
   * Set the sort option for notes
   */
  setSortOption = (option: ImportedNoteSortBy) => {
    this.sortOption = option;
  };

  /**
   * Toggle whether to use folders as collections
   */
  toggleUseFoldersAsCollections = () => {
    this.useFoldersAsCollections = !this.useFoldersAsCollections;
  };

  /**
   * Toggle whether to use tags as collections
   */
  toggleUseTagsAsCollections = () => {
    this.useTagsAsCollections = !this.useTagsAsCollections;
  };

  /**
   * Set selected folder names for conversion to collections
   */
  setSelectedFolderNames = (folderNames: string[]) => {
    this.selectedFolderNames = folderNames;
  };

  /**
   * Set selected tag names for conversion to collections
   */
  setSelectedTagNames = (tagNames: string[]) => {
    this.selectedTagNames = tagNames;
  };

  /**
   * Toggle selection of a note
   */
  toggleNoteSelection = (noteId: string) => {
    runInAction(() => {
      const note = this.notes.find(n => n.id === noteId);
      if (note && !note.isLocked) {
        note.isSelected = !note.isSelected;
      }
    });
  };

  /**
   * Select all selectable (non-locked) notes
   */
  selectAll = () => {
    runInAction(() => {
      this.notes.forEach(note => {
        if (!note.isLocked) {
          note.isSelected = true;
        }
      });
    });
  };

  /**
   * Unselect all notes
   */
  unselectAll = () => {
    runInAction(() => {
      this.notes.forEach(note => {
        note.isSelected = false;
      });
    });
  };

  /**
   * Toggle selection for all selectable notes in a section.
   * If any notes are unselected, selects all. Otherwise, deselects all.
   */
  toggleSectionSelection = (section: string) => {
    const group = this.notesGroupedByFolder.find(g => g.section === section);
    if (!group) return;

    const selectableNotes = group.notes.filter(note => !note.isLocked);
    const allSelected = selectableNotes.every(note => note.isSelected);

    runInAction(() => {
      selectableNotes.forEach(note => {
        note.isSelected = !allSelected;
      });
    });
  };

  /**
   * Select all selectable notes in a section
   */
  selectSection = (section: string) => {
    const group = this.notesGroupedByFolder.find(g => g.section === section);
    if (!group) return;

    runInAction(() => {
      group.notes.forEach(note => {
        if (!note.isLocked) {
          note.isSelected = true;
        }
      });
    });
  };

  /**
   * Unselect all notes in a section
   */
  unselectSection = (section: string) => {
    const group = this.notesGroupedByFolder.find(g => g.section === section);
    if (!group) return;

    runInAction(() => {
      group.notes.forEach(note => {
        note.isSelected = false;
      });
    });
  };

  /**
   * Get the selection state for a section
   */
  getSectionSelectionState = (section: string): SectionSelectionState => {
    const group = this.notesGroupedByFolder.find(g => g.section === section);
    if (!group) return SectionSelectionState.None;

    const selectableNotes = group.notes.filter(note => !note.isLocked);
    if (selectableNotes.length === 0) return SectionSelectionState.None;

    const selectedCount = selectableNotes.filter(note => note.isSelected).length;

    if (selectedCount === 0) return SectionSelectionState.None;
    if (selectedCount === selectableNotes.length) return SectionSelectionState.All;
    return SectionSelectionState.Indeterminate;
  };

  /**
   * Get or create a collection ID for a folder name
   */
  private getOrCreateCollectionId = (folderName: string): Uuid => {
    if (!this.folderToCollectionId.has(folderName)) {
      this.folderToCollectionId.set(folderName, uuidModule.generate());
    }
    return this.folderToCollectionId.get(folderName)!;
  };

  /**
   * Get or create a collection ID for a tag name
   */
  private getOrCreateTagCollectionId = (tagName: string): Uuid => {
    if (!this.tagToCollectionId.has(tagName)) {
      this.tagToCollectionId.set(tagName, uuidModule.generate());
    }
    return this.tagToCollectionId.get(tagName)!;
  };

  /**
   * Get the collection ID for a folder name (returns undefined if folder doesn't exist)
   */
  getCollectionIdForFolder = (folderName: string): Uuid | undefined => {
    return this.folderToCollectionId.get(folderName);
  };

  /**
   * Get the collection ID for a tag name (returns undefined if tag doesn't exist)
   */
  getCollectionIdForTag = (tagName: string): Uuid | undefined => {
    return this.tagToCollectionId.get(tagName);
  };

  /**
   * Get the count of notes that have a specific folder
   */
  getNotesCountForFolder = (folderName: string): number => {
    return this.notes.filter(note => note.folders?.includes(folderName)).length;
  };

  /**
   * Get the count of notes that have a specific tag
   */
  getNotesCountForTag = (tagName: string): number => {
    return this.notes.filter(note => note.tags?.includes(tagName)).length;
  };

  /**
   * Get the collection badges that should be displayed for a note.
   * Combines folder and tag badges, filters by enabled toggles and selections,
   * and deduplicates by name (case-insensitive).
   */
  getBadgesForNote = (note: ImportableNote): NoteCollectionBadge[] => {
    const badges: NoteCollectionBadge[] = [];

    // Add folder badges if enabled and in selected list
    if (this.useFoldersAsCollections && note.folders) {
      note.folders.forEach(folder => {
        if (this.selectedFolderNames.includes(folder)) {
          badges.push({
            type: "folder",
            name: folder,
            collectionId: this.getCollectionIdForFolder(folder),
          });
        }
      });
    }

    // Add tag badges if enabled and in selected list
    if (this.useTagsAsCollections && note.tags) {
      note.tags.forEach(tag => {
        if (this.selectedTagNames.includes(tag)) {
          badges.push({
            type: "tag",
            name: tag,
            collectionId: this.getCollectionIdForTag(tag),
          });
        }
      });
    }

    // Deduplicate by name (case-insensitive)
    const seenNames = new Set<string>();
    const uniqueBadges = badges.filter(badge => {
      const lowerName = badge.name.toLowerCase();
      if (seenNames.has(lowerName)) {
        return false;
      }
      seenNames.add(lowerName);
      return true;
    });

    return uniqueBadges;
  };

  /**
   * Filters out the default Apple Notes folder ("Notes") from the folder path.
   * - If "Notes" is the first folder in the path, it is removed
   * - If "Notes" is the only folder, returns an empty array (unfiled)
   * - If "Notes" appears elsewhere in the path, it is preserved
   */
  private filterDefaultAppleNotesFolder = (folders: string[]): string[] => {
    if (folders.length === 0) {
      return folders;
    }

    // Only filter if "Notes" is the first folder (exact match)
    if (folders[0] === AppStoreOneClickImportStore.DEFAULT_APPLE_NOTES_FOLDER) {
      return folders.slice(1);
    }

    return folders;
  };

  /**
   * Get the section name for a note (last folder in path)
   */
  private getSectionForNote = (note: ImportableNote): string => {
    if (!note.folders || note.folders.length === 0) {
      return "";
    }
    return note.folders[note.folders.length - 1];
  };

  /**
   * Sanitize a filename by removing invalid characters
   */
  private sanitizeFilename = (title: string): string => {
    return (
      title
        .replace(/[<>:"/\\|?*]/g, "-")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 200) || "Untitled"
    );
  };

  /**
   * Convert an ImportableNote to a FileDropItemWithPath for the import system
   */
  private createFileDropItemFromNote = (note: ImportableNote): FileDropItemWithPath => {
    // Build folder path with sanitized folder names (spaces -> underscores for file paths)
    // ["My Folder", "Work"] => "/My_Folder/Work"
    const sanitizedFolderPath = note.folders?.length
      ? `/${note.folders.map(folder => this.sanitizeFolderNameForPath(folder)).join("/")}`
      : "";
    const filename = `${this.sanitizeFilename(note.title)}.md`;
    const fullPath = `${sanitizedFolderPath}/${filename}`;

    const content = note.markdown;

    // Create File blob with preserved timestamp
    const lastModified = note.updatedAt ? new Date(note.updatedAt).getTime() : Date.now();

    return {
      kind: "file" as const,
      type: "text/markdown",
      name: filename,
      fullPath,
      getFile: async () =>
        new File([content], filename, {
          type: "text/markdown",
          lastModified,
        }),
      getText: async () => content,
    };
  };

  /**
   * Normalize folder name for use in hashtags.
   * Backend regex requires `#[a-zA-Z0-9_-]+`, so we replace spaces with underscores.
   * The collection title will preserve underscores (matching the folder name sanitization).
   */
  private normalizeFolderNameForHashtag = (folderName: string): string | undefined => {
    const trimmed = folderName.trim();
    if (trimmed.length === 0) return undefined;

    // Replace whitespace with underscores, strip unsupported chars to match backend regex.
    const normalized = trimmed.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_-]/g, "");
    if (normalized.length === 0) return undefined;

    return normalized;
  };

  /**
   * Sanitize folder name for use in file paths (replaces spaces with underscores).
   * This matches the sanitization used in hashtags so collection names stay consistent.
   */
  private sanitizeFolderNameForPath = (folderName: string): string => {
    return folderName.replace(/\s+/g, "_").replace(/[<>:"/\\|?*]/g, "-");
  };

  private replaceAttachmentPlaceholders = (
    markdown: string,
    attachmentIdToUrl: Map<string, string>,
    attachments: UploadableAttachment[],
    skippedAttachments: SkippedAttachment[] = []
  ): string => {
    const placeholderRegex = /(!?)\[([^\]]*)\]\(mem-import-attachment:\/\/([^)]+)\)/g;
    const attachmentMap = new Map(attachments.map(a => [a.id, a]));
    const skippedMap = new Map(skippedAttachments.map(s => [s.id, s]));

    return markdown.replace(placeholderRegex, (match, imagePrefix, altText, attachmentId) => {
      const url = attachmentIdToUrl.get(attachmentId);
      const attachment = attachmentMap.get(attachmentId);
      const skipped = skippedMap.get(attachmentId);

      // If attachment was skipped, replace with omission message
      if (skipped) {
        return `[Omitted (${skipped.reason}): ${skipped.filename}]`;
      }

      if (!url) {
        return match; // Upload failed, leave placeholder
      }

      // Images use markdown image syntax
      if (imagePrefix === "!") {
        return `![${altText}](${url})`;
      }

      // Non-image files use HTML with file data attributes (renders as download chip)
      if (attachment) {
        return buildFileAttachmentHtml({
          fileId: attachment.id,
          filename: attachment.filename,
          mimeType: attachment.mimeType,
          sizeBytes: attachment.sizeBytes,
          uploadUrl: url,
          displayText: altText,
          assetAccessTokenState: this.store.publicAppStore.auth.assetAccessTokenState,
        });
      }

      // Fallback to plain link if attachment metadata not available
      return `[${altText}](${url})`;
    });
  };

  /**
   * Import selected notes using the session-based import system.
   * For each note: upload its attachments, replace placeholders, then upload the note.
   *
   * Returns an ImportResult with success status and counts.
   * - success: true if at least one note was uploaded
   * - Partial failures are handled gracefully (proceeds with successful uploads)
   */
  importSelectedNotes = async (): Promise<ImportResult> => {
    const notes = this.selectedNotes;
    const totalSelected = notes.length;

    // Nothing selected, nothing to do
    if (totalSelected === 0) {
      return {
        success: false,
        importedCount: 0,
        failedCount: 0,
        totalSelected: 0,
        sessionId: null,
      };
    }

    const importStartTime = Date.now();
    const attachmentStats = this.getAttachmentStats({ notes: this.notes, totalSelected });

    this.setIsImporting(true);

    // Track import submission
    trackEvent(TrackedEvent.OneClickImportSubmit, {
      source: this.source,
      selected_count: totalSelected,
      total_count: this.totalNotesCount,
      entry_point: this.entryPoint ?? "profile_menu",
      use_folders_as_collections: this.useFoldersAsCollections,
      use_tags_as_collections: this.useTagsAsCollections,
      total_folders_count: this.folders.length,
      selected_folders_count: this.useFoldersAsCollections ? this.selectedFolderNames.length : 0,
      total_tags_count: this.tags.length,
      selected_tags_count: this.useTagsAsCollections ? this.selectedTagNames.length : 0,
    });

    const { sessionId, errors: sessionErrors } = await this.setupSessionForUpload({
      totalSelected,
    });

    // if somehow we failed at session syncops, retreat! Save your lives!
    if (!sessionId) {
      return this.processUploadFailure({
        importedCount: 0,
        failedCount: 0,
        totalSelected,
        sessionId,
        errors: sessionErrors,
        importStartTime,
        attachmentStats,
      });
    }

    const fileItems = await this.loadAttachments({ notes });

    // Create batch with config
    const batchId = await this.store.uploadedFileBatches.createUploadedFileBatch({
      sessionId,
      config: {
        use_filenames_as_titles: true,
        use_folders_as_collections: this.useFoldersAsCollections,
        use_tags_as_collections: this.useTagsAsCollections,
        selected_folder_names: this.useFoldersAsCollections ? [...this.selectedFolderNames] : [],
        selected_tag_names: this.useTagsAsCollections ? [...this.selectedTagNames] : [],
      },
    });

    // Upload files with partial success handling
    const uploadResult = await this.store.uploadProgress.uploadFileItems(fileItems, sessionId, {
      uploader: ImportFileUploaderKind.StorageApi,
      allowPartialSuccess: true,
    });

    const { successfulIds, failedCount, errors } = uploadResult;
    const importedCount = successfulIds.length;

    // Handle complete failure (all uploads failed)
    if (importedCount === 0) {
      return this.processUploadFailure({
        importedCount,
        failedCount,
        totalSelected,
        sessionId,
        errors,
        importStartTime,
        attachmentStats,
      });
    }

    // Some uploads failed
    if (failedCount > 0 && importedCount > 0) {
      await this.store.sessions.updateSessionOnNewUserMessage(sessionId, {
        title: `Bring in ${importedCount} ${pluralize(importedCount, "note", "notes")} from Apple Notes`,
        status: "PROCESSING",
      });

      await this.store.sessionEvents.createMessageSentSessionEvent({
        session_id: sessionId,
        message_value: {
          kind: "REASONING",
          content: `Uploaded ${importedCount} ${pluralize(importedCount, "note", "notes")}`,
        },
      });

      toastModule.triggerToast({
        content: `Some notes (${failedCount} of ${totalSelected}) could not be uploaded`,
      });

      logger.warn({
        message: `[importSelectedNotes] ${errors.length} note uploads partially failed`,
        info: {
          errors: errors.map(e => ({
            filename: e.filename,
            error: objectModule.safeErrorAsJson(e.error),
            sessionId,
            batchId,
          })),
        },
      });
    }

    /* --- From here we proceed as if everything went well --- */

    await clearImportSessionInProgress(sessionId);

    const uploadedFileResults = this.store.uploadProgress.getFileResults(successfulIds);
    await this.store.uploadedFiles.createUploadedFiles({ batchId, uploadedFileResults });
    await this.store.uploadedFileBatches.addUploadsToBatch(batchId, successfulIds);
    await this.store.uploadedFileBatches.startProcessingBatch(batchId, sessionId);

    // Register to navigate to imported notes when processing completes
    this.store.uploadedFileBatches.navigateToImportedNotesOnCompletion(batchId);

    trackEvent(TrackedEvent.ImportFilesBatchStarted, {
      num_files: uploadedFileResults.length,
      batch_id: batchId,
      session_id: sessionId,
    });

    this.trackImportComplete({
      success: true,
      importedCount,
      durationMs: Date.now() - importStartTime,
      attachmentStats,
    });

    // Upload phase complete - set isImporting = false
    this.setIsImporting(false);

    return {
      success: true,
      importedCount,
      failedCount,
      totalSelected,
      sessionId,
    };
  };

  private setupSessionForUpload = async ({
    totalSelected,
  }: {
    totalSelected: number;
  }): Promise<{ sessionId: string | null; errors: { filename: string; error: Error }[] }> => {
    let sessionId: string | null = null;
    try {
      sessionId = await this.store.sessions.createSession({
        title: `Bring in ${totalSelected} ${pluralize(totalSelected, "note", "notes")} from Apple Notes`,
        status: "PROCESSING",
        kind: "IMPORT",
      });

      await markImportSessionInProgress(sessionId);

      await this.store.sessionEvents.createMessageSentSessionEvent({
        session_id: sessionId,
        message_value: {
          kind: "REASONING",
          content: `Uploading ${totalSelected} ${pluralize(totalSelected, "note", "notes")}`,
        },
      });

      return { sessionId, errors: [] };
    } catch (e) {
      return { sessionId: null, errors: [{ filename: "", error: e as Error }] };
    }
  };

  /** In case upload failed completely and miserably */
  private processUploadFailure = async ({
    importedCount,
    failedCount,
    totalSelected,
    sessionId,
    errors,
    importStartTime,
    attachmentStats,
  }: {
    importedCount: number;
    failedCount: number;
    totalSelected: number;
    sessionId: string | null;
    attachmentStats: {
      notesWithImages: number;
      notesWithFiles: number;
      totalImages: number;
      totalFiles: number;
    };
    importStartTime: number;
    errors: {
      filename: string;
      error: Error;
    }[];
  }): Promise<ImportResult> => {
    // Cleanup session
    if (sessionId) {
      await this.store.sessions.updateSessionOnNewUserMessage(sessionId, { status: "ERROR" });
      await clearImportSessionInProgress(sessionId);

      this.store.sessionEvents.createMessageSentSessionEvent({
        session_id: sessionId,
        message_value: { kind: "REASONING", content: "Failed to upload notes" },
      });
    }

    trackEvent(TrackedEvent.ImportFilesBatchFailed, {
      num_files: totalSelected,
      session_id: sessionId,
      context: "oneClickImport",
      error:
        errors.length > 0 ? objectModule.safeErrorAsJson(errors[0].error) : "All uploads failed",
    });

    const durationMs = Date.now() - importStartTime;
    this.trackImportComplete({
      success: false,
      importedCount,
      durationMs,
      attachmentStats,
    });

    this.setIsImporting(false);

    this.setErrorMessage("Failed to import notes. Please try again.");

    logger.error({
      message: `[importSelectedNotes] ${errors.length} note uploads fully failed`,
      info: {
        sessionId,
        totalSelected,
        attachmentStats,
        durationMs,
        errors: errors.map(e => ({
          filename: e.filename,
          error: objectModule.safeErrorAsJson(e.error),
        })),
      },
    });

    return {
      success: false,
      importedCount: 0,
      failedCount,
      totalSelected,
      sessionId,
    };
  };

  private getAttachmentStats = ({
    notes,
  }: {
    notes: ImportableNote[];
    totalSelected: number;
  }): {
    notesWithImages: number;
    notesWithFiles: number;
    totalImages: number;
    totalFiles: number;
  } => {
    // Calculate attachment stats for analytics
    return notes.reduce(
      (acc, note) => {
        const attachments = note.attachments ?? [];
        const images = attachments.filter(a => a.mimeType.startsWith("image/"));
        const files = attachments.filter(a => !a.mimeType.startsWith("image/"));

        return {
          notesWithImages: acc.notesWithImages + (images.length > 0 ? 1 : 0),
          notesWithFiles: acc.notesWithFiles + (files.length > 0 ? 1 : 0),
          totalImages: acc.totalImages + images.length,
          totalFiles: acc.totalFiles + files.length,
        };
      },
      { notesWithImages: 0, notesWithFiles: 0, totalImages: 0, totalFiles: 0 }
    );
  };

  private loadAttachments = async ({
    notes,
  }: {
    notes: ImportableNote[];
  }): Promise<FileDropItemWithPath[]> => {
    const fileItems: FileDropItemWithPath[] = [];

    try {
      // Ensure asset access token is available for proxying attachment URLs
      // This is needed so file downloads work across all platforms (including iOS)
      await this.store.publicAppStore.auth.assetAccessTokenState.ensureToken();

      for (const note of notes) {
        let attachments = note.attachments ?? [];
        let electronSkipped = note.skippedAttachments ?? [];
        let markdown = note.markdown;

        const hasPlaceholder = markdown.includes("mem-import-attachment://");
        const appleNoteId = note.appleNoteId;
        if (
          isAppleNoteId(appleNoteId) &&
          (note.attachments === undefined ||
            note.skippedAttachments === undefined ||
            hasPlaceholder)
        ) {
          try {
            const noteContent = await electronModule.getAppleNoteContent(appleNoteId);
            attachments = noteContent.attachments;
            electronSkipped = noteContent.skippedAttachments;
            markdown = noteContent.content;
          } catch (e) {
            logger.error({
              message: `[importSelectedNotes] Failed to fetch attachments for note "${note.title}"`,
              info: {
                appleNoteId: note.appleNoteId ?? null,
                error: objectModule.safeErrorAsJson(e as Error),
              },
            });
          }
        }

        const { uploaded: attachmentIdToUrl, skipped: uploadSkipped } = await uploadAttachments({
          attachments,
        });

        const allSkipped = [...electronSkipped, ...uploadSkipped];

        if (allSkipped.length > 0) {
          logger.warn({
            message: `[importSelectedNotes] Skipped ${allSkipped.length} attachments in note "${note.title}"`,
            info: {
              electronSkipped: electronSkipped.length,
              uploadSkipped: uploadSkipped.length,
              skippedAttachments: allSkipped.map(s => ({
                id: s.id,
                filename: s.filename,
                reason: s.reason,
              })),
            },
          });
        }

        const resolvedMarkdown = this.replaceAttachmentPlaceholders(
          markdown,
          attachmentIdToUrl,
          attachments,
          allSkipped
        );

        fileItems.push(this.createFileDropItemFromNote({ ...note, markdown: resolvedMarkdown }));
      }
    } finally {
      await this.closeAppleNotesReader();
    }

    return fileItems;
  };

  private trackImportComplete({
    success,
    importedCount,
    durationMs,
    attachmentStats,
  }: {
    success: boolean;
    importedCount: number;
    durationMs: number;
    attachmentStats: {
      notesWithImages: number;
      notesWithFiles: number;
      totalImages: number;
      totalFiles: number;
    };
  }) {
    trackEvent(TrackedEvent.OneClickImportComplete, {
      source: this.source,
      success,
      entry_point: this.entryPoint ?? "profile_menu",
      imported_count: importedCount,
      duration_ms: durationMs,
      notes_with_images_count: attachmentStats.notesWithImages,
      notes_with_files_count: attachmentStats.notesWithFiles,
      total_images_count: attachmentStats.totalImages,
      total_files_count: attachmentStats.totalFiles,
    });
  }

  /**
   * Add a note to the import list
   */
  addNote = (note: Omit<ImportableNote, "id"> & { id?: string }) => {
    runInAction(() => {
      const newNote: ImportableNote = {
        id: note.id || uuidModule.generate(),
        title: note.title,
        markdown: note.markdown,
        folders: note.folders,
        isSelected: note.isSelected,
        isLocked: note.isLocked,
        lockedReason: note.lockedReason,
        isPinned: note.isPinned ?? false,
        tags: note.tags ?? [],
        createdAt: note.createdAt,
        updatedAt: note.updatedAt,
        subtitle: note.subtitle,
        appleNoteId: note.appleNoteId,
        attachments: note.attachments,
        skippedAttachments: note.skippedAttachments,
      };
      this.notes.push(newNote);
      this.noteTitles.push(newNote.title);

      // Generate collection IDs for all folders in the path
      if (note.folders && note.folders.length > 0) {
        note.folders.forEach(folder => {
          this.getOrCreateCollectionId(folder);
        });
      }

      // Generate collection IDs for all tags
      if (note.tags && note.tags.length > 0) {
        note.tags.forEach(tag => {
          this.getOrCreateTagCollectionId(tag);
        });
      }
    });
  };

  /**
   * Add multiple notes to the import list
   */
  addNotes = (notes: Array<Omit<ImportableNote, "id"> & { id?: string }>) => {
    notes.forEach(note => this.addNote(note));
  };

  /**
   * Clear all notes and reset state
   */
  clear = () => {
    runInAction(() => {
      this.notes = [];
      this.noteTitles = [];
      this.folderToCollectionId.clear();
      this.tagToCollectionId.clear();
      this.useFoldersAsCollections = true;
      this.useTagsAsCollections = true;
      this.selectedFolderNames = [];
      this.selectedTagNames = [];
      this.filterQuery = "";
      this.tagSearchQuery = "";
      this.folderSearchQuery = "";
      this.entryPoint = null;
    });
  };

  /**
   * Get selected notes
   */
  get selectedNotes(): ImportableNote[] {
    return this.notes.filter(note => note.isSelected);
  }

  /**
   * Get count of selected notes
   */
  get selectedNotesCount(): number {
    return this.selectedNotes.length;
  }

  /**
   * Get count of total notes
   */
  get totalNotesCount(): number {
    return this.notes.length;
  }

  /**
   * Get count of selectable (non-locked) notes
   */
  get selectableNotesCount(): number {
    return this.notes.filter(note => !note.isLocked).length;
  }

  /**
   * Get notes sorted by updatedAt (most recent first)
   */
  get sortedNotes(): ImportableNote[] {
    return [...this.notes].sort((a, b) => {
      if (!a.updatedAt && !b.updatedAt) return 0;
      if (!a.updatedAt) return 1;
      if (!b.updatedAt) return -1;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }

  /**
   * Get notes filtered by the search query (fuzzy matching on titles).
   * Uses pre-computed noteTitles array for optimal performance.
   */
  get filteredNotes(): ImportableNote[] {
    if (!this.filterQuery.trim()) {
      return this.notes;
    }

    const indexes = this.fuzzy.filter(this.noteTitles, this.filterQuery);

    if (!indexes || indexes.length === 0) {
      return [];
    }

    return indexes.map(index => this.notes[index]);
  }

  /**
   * Get unique folder names from all notes (flattened from all folder paths)
   */
  get folders(): string[] {
    const folderSet = new Set<string>();
    this.notes.forEach(note => {
      if (note.folders) {
        note.folders.forEach(folder => folderSet.add(folder));
      }
    });
    return Array.from(folderSet).sort();
  }

  /**
   * Get unique tag names from all notes
   */
  get tags(): string[] {
    const tagSet = new Set<string>();
    this.notes.forEach(note => {
      if (note.tags) {
        note.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }

  /**
   * Get filtered folders using fuzzy search
   */
  get filteredFolders(): string[] {
    if (!this.folderSearchQuery.trim()) {
      return this.folders;
    }

    const indexes = this.fuzzy.filter(this.folders, this.folderSearchQuery);

    if (!indexes || indexes.length === 0) {
      return [];
    }

    return indexes.map(index => this.folders[index]);
  }

  /**
   * Get filtered tags using fuzzy search
   */
  get filteredTags(): string[] {
    if (!this.tagSearchQuery.trim()) {
      return this.tags;
    }

    const indexes = this.fuzzy.filter(this.tags, this.tagSearchQuery);

    if (!indexes || indexes.length === 0) {
      return [];
    }

    return indexes.map(index => this.tags[index]);
  }

  /**
   * Get folders with their associated collection IDs
   */
  get importableFolders(): ImportableFolder[] {
    return this.folders.map(name => ({
      name,
      collectionId: this.folderToCollectionId.get(name)!,
    }));
  }

  /**
   * Get tags with their associated collection IDs
   */
  get importableTags(): ImportableFolder[] {
    return this.tags.map(name => ({
      name,
      collectionId: this.tagToCollectionId.get(name)!,
    }));
  }

  /**
   * Get notes grouped by section (last folder in path), sorted according to sortOption.
   * Notes without folders are grouped under an empty string key.
   * Both notes within sections and sections themselves are sorted based on sortOption.
   * Empty sections (after filtering) are not included.
   * The unlisted section is always shown, even when there are no other folders.
   */
  get notesGroupedByFolder(): FolderGroup[] {
    const groups = new Map<string, ImportableNote[]>();

    // Group filtered notes by section (last folder)
    this.filteredNotes.forEach(note => {
      const section = this.getSectionForNote(note);
      if (!groups.has(section)) {
        groups.set(section, []);
      }
      groups.get(section)!.push(note);
    });

    // Sort notes within each group based on sortOption
    groups.forEach(notes => {
      switch (this.sortOption) {
        case SortByKind.LastModified:
          notes.sort((a, b) => {
            if (!a.updatedAt && !b.updatedAt) return 0;
            if (!a.updatedAt) return 1;
            if (!b.updatedAt) return -1;
            return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
          });
          break;
        case SortByKind.LastCreated:
          notes.sort((a, b) => {
            if (!a.createdAt && !b.createdAt) return 0;
            if (!a.createdAt) return 1;
            if (!b.createdAt) return -1;
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          });
          break;
        case SortByKind.Alphabetical:
          notes.sort((a, b) => {
            return a.title.localeCompare(b.title, undefined, { sensitivity: "base" });
          });
          break;
      }
    });

    // Convert to array and sort sections based on sortOption
    // Always put "Unfiled" (empty string section) at the bottom
    const result: FolderGroup[] = [];
    const allSections = Array.from(groups.keys());
    const unfiledSection = allSections.find(section => section === "");
    const namedSections = allSections.filter(section => section !== "");

    const sortedSections = namedSections.sort((a, b) => {
      const notesA = groups.get(a)!;
      const notesB = groups.get(b)!;

      switch (this.sortOption) {
        case SortByKind.LastModified: {
          // Sort sections by most recent updatedAt in each section
          const mostRecentA = notesA.reduce((latest, note) => {
            if (!note.updatedAt) return latest;
            if (!latest) return note.updatedAt;
            return new Date(note.updatedAt) > new Date(latest) ? note.updatedAt : latest;
          }, notesA[0]?.updatedAt);

          const mostRecentB = notesB.reduce((latest, note) => {
            if (!note.updatedAt) return latest;
            if (!latest) return note.updatedAt;
            return new Date(note.updatedAt) > new Date(latest) ? note.updatedAt : latest;
          }, notesB[0]?.updatedAt);

          if (!mostRecentA && !mostRecentB) return 0;
          if (!mostRecentA) return 1;
          if (!mostRecentB) return -1;
          return new Date(mostRecentB).getTime() - new Date(mostRecentA).getTime();
        }
        case SortByKind.LastCreated: {
          // Sort sections by most recent createdAt in each section
          const mostRecentA = notesA.reduce((latest, note) => {
            if (!note.createdAt) return latest;
            if (!latest) return note.createdAt;
            return new Date(note.createdAt) > new Date(latest) ? note.createdAt : latest;
          }, notesA[0]?.createdAt);

          const mostRecentB = notesB.reduce((latest, note) => {
            if (!note.createdAt) return latest;
            if (!latest) return note.createdAt;
            return new Date(note.createdAt) > new Date(latest) ? note.createdAt : latest;
          }, notesB[0]?.createdAt);

          if (!mostRecentA && !mostRecentB) return 0;
          if (!mostRecentA) return 1;
          if (!mostRecentB) return -1;
          return new Date(mostRecentB).getTime() - new Date(mostRecentA).getTime();
        }
        case SortByKind.Alphabetical:
          // Sort sections alphabetically (case-insensitive)
          return a.localeCompare(b, undefined, { sensitivity: "base" });
        default:
          // Default to alphabetical sort
          return a.localeCompare(b, undefined, { sensitivity: "base" });
      }
    });

    // Add unfiled section at the end if it exists
    if (unfiledSection !== undefined) {
      sortedSections.push(unfiledSection);
    }

    sortedSections.forEach(section => {
      const notes = groups.get(section)!;
      // Get collection IDs from the first note's folders (all notes in same section share the same path)
      const firstNote = notes[0];
      const collectionIds =
        firstNote.folders?.map(folder => this.folderToCollectionId.get(folder)!) || [];

      result.push({
        section,
        collectionIds,
        notes,
      });
    });

    return result;
  }
}
